// 塗りつぶし・グラデーションの純ロジック (CPU)。
// すべてストレートアルファ RGBA、座標は呼び出し側が揃える。

import type { Pt, Rect } from '@/types/geometry'
import type { MaskData } from '@/selection/mask'
import { maskAt } from '@/selection/mask'

/**
 * dst (rect 範囲のレイヤー画素) の上に src を source-over で合成する (両方ストレートアルファ)。
 * alphaScale で src の α を一律に掛ける。lockTransparency なら dst の α を保つ。
 * dst を書き換えて返す。
 */
export function sourceOverInPlace(dst: Uint8ClampedArray, src: Uint8ClampedArray, alphaScale = 1, lockTransparency = false): Uint8ClampedArray {
  const n = Math.min(dst.length, src.length)
  for (let i = 0; i < n; i += 4) {
    let as = (src[i + 3]! / 255) * alphaScale
    if (as <= 0) continue
    const ab = dst[i + 3]! / 255
    if (lockTransparency) as *= ab
    const ao = as + ab * (1 - as)
    if (ao <= 0) continue
    for (let c = 0; c < 3; c++) {
      dst[i + c] = Math.round((src[i + c]! * as + dst[i + c]! * ab * (1 - as)) / ao)
    }
    dst[i + 3] = Math.round((lockTransparency ? ab : ao) * 255)
  }
  return dst
}

/** マスク (文書座標) を rect (文書座標) の範囲で α 配列として展開する。マスクなしは全 255 */
export function maskToAlpha(mask: MaskData | null, rect: Rect): Uint8ClampedArray {
  const out = new Uint8ClampedArray(rect.w * rect.h)
  if (!mask) return out.fill(255)
  for (let y = 0; y < rect.h; y++) for (let x = 0; x < rect.w; x++) out[y * rect.w + x] = maskAt(mask, rect.x + x, rect.y + y)
  return out
}

/** 単色 × α 配列 → RGBA */
export function solidWithAlpha(rect: Rect, color: [number, number, number], alpha: Uint8ClampedArray, opacity = 1): Uint8ClampedArray {
  const out = new Uint8ClampedArray(rect.w * rect.h * 4)
  for (let i = 0; i < rect.w * rect.h; i++) {
    out[i * 4] = color[0]
    out[i * 4 + 1] = color[1]
    out[i * 4 + 2] = color[2]
    out[i * 4 + 3] = Math.round(alpha[i]! * opacity)
  }
  return out
}

export type GradientStyle = 'linear' | 'radial' | 'angle' | 'reflected' | 'diamond'

export interface GradientStop {
  /** 0..1 */
  pos: number
  color: [number, number, number]
  alpha: number
}

export interface GradientSpec {
  style: GradientStyle
  stops: GradientStop[]
  reverse: boolean
  dither: boolean
}

function sampleStops(stops: GradientStop[], t: number): [number, number, number, number] {
  if (!stops.length) return [0, 0, 0, 0]
  if (t <= stops[0]!.pos) return [...stops[0]!.color, stops[0]!.alpha]
  for (let i = 0; i + 1 < stops.length; i++) {
    const a = stops[i]!, b = stops[i + 1]!
    if (t <= b.pos) {
      const k = b.pos === a.pos ? 0 : (t - a.pos) / (b.pos - a.pos)
      return [a.color[0] + (b.color[0] - a.color[0]) * k, a.color[1] + (b.color[1] - a.color[1]) * k, a.color[2] + (b.color[2] - a.color[2]) * k, a.alpha + (b.alpha - a.alpha) * k]
    }
  }
  const last = stops[stops.length - 1]!
  return [...last.color, last.alpha]
}

/** 位置 p に対するグラデーションのパラメータ t (0..1、範囲外はクランプ) */
export function gradientT(style: GradientStyle, a: Pt, b: Pt, p: Pt): number {
  const dx = b.x - a.x, dy = b.y - a.y
  const len2 = dx * dx + dy * dy
  const px = p.x - a.x, py = p.y - a.y
  let t: number
  if (len2 === 0) return 0
  switch (style) {
    case 'linear':
      t = (px * dx + py * dy) / len2
      break
    case 'radial':
      t = Math.sqrt(px * px + py * py) / Math.sqrt(len2)
      break
    case 'angle': {
      const base = Math.atan2(dy, dx)
      let ang = Math.atan2(py, px) - base
      ang = ((ang % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2)
      t = ang / (Math.PI * 2)
      break
    }
    case 'reflected':
      t = Math.abs((px * dx + py * dy) / len2)
      break
    case 'diamond': {
      const len = Math.sqrt(len2)
      const ux = dx / len, uy = dy / len
      const along = Math.abs(px * ux + py * uy)
      const perp = Math.abs(-px * uy + py * ux)
      t = Math.max(along, perp) / len
      break
    }
  }
  return t < 0 ? 0 : t > 1 ? 1 : t
}

/** rect (文書座標) の RGBA を生成する。alpha は選択マスクなどの追加 α (省略可) */
export function renderGradient(rect: Rect, a: Pt, b: Pt, spec: GradientSpec, alpha?: Uint8ClampedArray, opacity = 1): Uint8ClampedArray {
  const out = new Uint8ClampedArray(rect.w * rect.h * 4)
  const stops = spec.reverse ? [...spec.stops].map(s => ({ ...s, pos: 1 - s.pos })).sort((p, q) => p.pos - q.pos) : spec.stops
  let seed = 1234567
  const rnd = () => {
    seed = (seed * 1664525 + 1013904223) >>> 0
    return seed / 4294967296 - 0.5
  }
  for (let y = 0; y < rect.h; y++) {
    for (let x = 0; x < rect.w; x++) {
      const t = gradientT(spec.style, a, b, { x: rect.x + x + 0.5, y: rect.y + y + 0.5 })
      const [r, g, bl, al] = sampleStops(stops, t)
      const i = (y * rect.w + x) * 4
      const d = spec.dither ? rnd() : 0
      out[i] = r + d
      out[i + 1] = g + d
      out[i + 2] = bl + d
      out[i + 3] = al * 255 * opacity * (alpha ? alpha[y * rect.w + x]! / 255 : 1)
    }
  }
  return out
}
